import React from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FileText, ImageIcon, Briefcase, Sparkles,
  Inbox, Settings, BookOpen, LogOut, Loader2, ExternalLink, Users, FilePlus2, Menu as MenuIcon,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BASE_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon },
  { to: "/admin/pages", label: "Pages", icon: FilePlus2 },
  { to: "/admin/navigation", label: "Navigation", icon: MenuIcon },
  { to: "/admin/hero", label: "Hero (Home)", icon: Sparkles },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/about", label: "About Page", icon: FileText },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/help", label: "How to Edit", icon: BookOpen },
];

const SUPER_ADMIN_ITEM = { to: "/admin/users", label: "Users", icon: Users, superOnly: true };

const buildNav = (user) => {
  if (user?.role === "super_admin") {
    // Insert "Users" right before "How to Edit"
    return [...BASE_NAV.slice(0, -1), SUPER_ADMIN_ITEM, BASE_NAV[BASE_NAV.length - 1]];
  }
  return BASE_NAV;
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F]">
        <Loader2 className="text-[#D4AF37] animate-spin" size={32} />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const NAV = buildNav(user);

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A192F] text-white flex flex-col fixed inset-y-0 left-0 z-40 hidden lg:flex">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="font-display font-black text-lg tracking-tight">CM LINDLEY</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">Admin Console</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                    isActive
                      ? "bg-white/5 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                      : "text-white/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={16} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-6 border-t border-white/10 space-y-3 text-sm">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <ExternalLink size={14} /> View Live Site
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
          <div className="text-[10px] text-white/30 pt-2 border-t border-white/10">
            {user.email}
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-[#0A192F] text-white px-4 py-3 flex items-center justify-between z-40">
        <div className="font-display font-black text-sm">CM LINDLEY ADMIN</div>
        <button
          onClick={logout}
          data-testid="admin-logout-mobile"
          className="text-white/70 hover:text-[#D4AF37]"
        >
          <LogOut size={16} />
        </button>
      </div>
      <div className="lg:hidden fixed top-12 left-0 right-0 bg-white border-b border-gray-200 flex overflow-x-auto z-30">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `flex-shrink-0 px-4 py-3 text-xs font-display uppercase tracking-wider font-bold border-b-2 transition-colors ${
                isActive ? "border-[#D4AF37] text-[#0A192F]" : "border-transparent text-[#0A192F]/60"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </div>

      <main className="flex-1 lg:ml-64 pt-24 lg:pt-0">
        <div className="max-w-6xl mx-auto p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
